import type { FieldErrors, FieldValues } from 'react-hook-form'

interface FormErrorSummaryProps<TFieldValues extends FieldValues> {
  errors: FieldErrors<TFieldValues>
  serverErrors?: Record<string, string[]> | null
  /** Shown only when there's no field-specific error to list — e.g. login's
   * generic "Invalid email or password." or a rate-limit message. */
  generalMessage?: string | null
}

/**
 * Validation summary pinned at the top of a form (Frontend Spec §6.4, job
 * posting page) — merges client-side RHF errors with server-side
 * ApiError.fieldErrors so both surface in one place.
 */
export function FormErrorSummary<TFieldValues extends FieldValues>({
  errors,
  serverErrors,
  generalMessage,
}: FormErrorSummaryProps<TFieldValues>) {
  const clientMessages = Object.values(errors)
    .map((entry) =>
      entry && typeof entry === 'object' && 'message' in entry ? (entry.message as string) : null,
    )
    .filter((message): message is string => !!message)

  const serverMessages = serverErrors ? Object.values(serverErrors).flat() : []
  const messages = [...clientMessages, ...serverMessages]

  if (messages.length === 0) {
    if (!generalMessage) return null
    return (
      <div className="alert alert-danger" role="alert">
        {generalMessage}
      </div>
    )
  }

  return (
    <div className="alert alert-danger" role="alert">
      <p className="fw-medium mb-1">Please fix the following:</p>
      <ul className="mb-0 ps-3">
        {messages.map((message, idx) => (
          <li key={idx}>{message}</li>
        ))}
      </ul>
    </div>
  )
}
